import React, { useState, useCallback, useMemo } from 'react';
import { GoogleMap, useLoadScript, Marker, InfoWindow, MarkerClusterer } from '@react-google-maps/api';
import { Link } from 'react-router-dom';
import BounceLoader from 'react-spinners/BounceLoader';

const containerStyle = {
    width: '100%',
    height: '600px',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
};

const defaultCenter = {
    lat: 20.5937,
    lng: 78.9629 // Center of India
};

const options = {
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
};

const ApiaryMap = ({ apiaries }) => {
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: "AIzaSyBg4tz2fOqTqAny-Hph8blHRP9YeRTuIDg", // Using key from MapComponent.js
    });

    const [map, setMap] = useState(null);
    const [selectedApiary, setSelectedApiary] = useState(null);

    // Helper to extract valid coordinates
    const getCoordinates = (apiary) => {
        let lat, lng;

        // Check geo_location object (based on user logs)
        if (apiary.geo_location && apiary.geo_location.lat && apiary.geo_location.lng) {
            lat = parseFloat(apiary.geo_location.lat);
            lng = parseFloat(apiary.geo_location.lng);
        }
        // Fallback to top-level fields
        else if (apiary.latitude && apiary.longitude) {
            lat = parseFloat(apiary.latitude);
            lng = parseFloat(apiary.longitude);
        } else if (apiary.lat && apiary.lng) {
            lat = parseFloat(apiary.lat);
            lng = parseFloat(apiary.lng);
        }

        if (!isNaN(lat) && !isNaN(lng)) {
            return { lat, lng };
        }
        return null;
    };

    const validApiaries = useMemo(() => {
        const processed = [];
        const coordMap = new Map(); // Track coordinates to handle duplicates

        apiaries.forEach(apiary => {
            const coords = getCoordinates(apiary);
            if (coords) {
                let { lat, lng } = coords;
                const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;

                // If these coordinates already exist, add a smart jitter
                if (coordMap.has(key)) {
                    const count = coordMap.get(key);

                    // Jitter ~0.05 degrees (approx 5.5 km)
                    // This ensures visibility at Zoom 5 (Country Level)
                    // Spiral pattern:
                    // Radius increases every 6-8 items
                    // Angle increments by 45-60 degrees
                    const angle = count * (Math.PI / 4); // 45 degrees
                    const radius = 0.05 * (1 + Math.floor(count / 8)); // ~5.5km base radius, increases slightly

                    lat += Math.cos(angle) * radius;
                    lng += Math.sin(angle) * radius;

                    coordMap.set(key, count + 1);
                } else {
                    coordMap.set(key, 1);
                }

                processed.push({ ...apiary, lat, lng });
            }
        });

        // Debug Log to check coordinate spread
        console.log(`🗺️ Map: Found ${processed.length} valid apiaries out of ${apiaries.length}`);
        if (processed.length > 0) {
            console.log('Sample Jitter Check:', processed.slice(0, 5).map(p => ({ id: p.apiary_id, lat: p.lat, lng: p.lng })));
        }

        return processed;
    }, [apiaries]);

    const onLoad = useCallback((map) => {
        if (validApiaries.length === 0) return;

        const bounds = new window.google.maps.LatLngBounds();
        validApiaries.forEach(apiary => {
            bounds.extend({ lat: apiary.lat, lng: apiary.lng });
        });
        map.fitBounds(bounds);
        setMap(map);
    }, [validApiaries]);

    const onUnmount = useCallback(() => {
        setMap(null);
    }, []);

    const getMarkerIcon = (apiary) => {
        const isStorage = apiary.apiary_type === 'Storage';
        const isField = !apiary.apiary_type || apiary.apiary_type === 'Field';
        
        // Colors from ApiaryTable pill design
        const color = isStorage ? "#0984e3" : (isField ? "#00b894" : "#d63031");

        // Larger Red Flag Icon
        return {
            path: "M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z", // Material Design Flag Icon
            fillColor: color,
            fillOpacity: 1,
            strokeWeight: 1.5,
            strokeColor: "#ffffff",
            scale: 2.0,
            anchor: new window.google.maps.Point(5, 20),
        };
    };

    if (loadError) {
        return <div className="p-4 text-center text-danger">Error loading maps: {loadError.message}</div>;
    }

    if (!isLoaded) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '600px', background: '#f8f9fa', borderRadius: '12px' }}>
                <BounceLoader color="#ff8701" loading={true} size={40} />
            </div>
        );
    }

    return (
        <div style={{ position: 'relative', height: '600px', width: '100%', overflow: 'hidden', borderRadius: '12px' }}>
            {validApiaries.length === 0 && (
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                    background: 'rgba(255, 255, 255, 0.95)',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    textAlign: 'center'
                }}>
                    <h5 style={{ margin: 0, color: '#636e72' }}>No map locations found</h5>
                    <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#b2bec3' }}>
                        None of the {apiaries.length} apiaries have valid coordinate data.
                    </p>
                </div>
            )}

            <GoogleMap
                mapContainerStyle={containerStyle}
                center={defaultCenter}
                zoom={5}
                onLoad={onLoad}
                onUnmount={onUnmount}
                options={options}
                onClick={() => setSelectedApiary(null)} // Close sidebar when map is clicked
            >
                {/* Removed MarkerClusterer to show all flags individually */}
                {validApiaries.map((apiary) => (
                    <Marker
                        key={apiary.apiary_id}
                        position={{ lat: apiary.lat, lng: apiary.lng }}
                        onClick={(e) => {
                            e.stop(); // Prevent map click
                            setSelectedApiary(apiary);
                        }}
                        icon={getMarkerIcon(apiary)}
                        title={apiary.apiary_name}
                    />
                ))}
            </GoogleMap>

            {/* Sidebar Details Panel */}
            <div style={{
                position: 'absolute',
                top: 0,
                right: selectedApiary ? 0 : '-320px', // Slide in/out
                width: '320px',
                height: '100%',
                background: '#ffffff',
                boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
                transition: 'right 0.3s ease-in-out',
                zIndex: 20,
                display: 'flex',
                flexDirection: 'column'
            }}>
                {selectedApiary && (
                    <>
                        <div style={{
                            padding: '16px',
                            borderBottom: '1px solid #f0f0f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            background: '#f8f9fa'
                        }}>
                            <h5 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold', color: '#2d3436' }}>Apiary Details</h5>
                            <button
                                onClick={() => setSelectedApiary(null)}
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    fontSize: '20px',
                                    cursor: 'pointer',
                                    color: '#636e72'
                                }}
                            >
                                &times;
                            </button>
                        </div>

                        <div style={{ padding: '20px', flex: 1, overflowY: 'auto' }}>
                            <h4 style={{ fontSize: '18px', fontWeight: 'bold', color: '#2d3436', marginBottom: '8px' }}>
                                {selectedApiary.apiary_name}
                            </h4>
                            <div style={{
                                display: 'inline-block',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: '600',
                                marginBottom: '16px',
                                background: selectedApiary.is_active === false ? '#fff5f5' : '#e6fffa',
                                color: selectedApiary.is_active === false ? '#d63031' : '#00b894',
                                border: `1px solid ${selectedApiary.is_active === false ? '#ffa8a8' : '#b2f2bb'}`
                            }}>
                                {selectedApiary.is_active === false ? 'Disabled' : (selectedApiary.operational_status || 'Active')}
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ fontSize: '12px', color: '#b2bec3', textTransform: 'uppercase', fontWeight: 'bold' }}>Location</label>
                                <div style={{ fontSize: '14px', color: '#636e72' }}>
                                    {selectedApiary.village || 'N/A'}, {selectedApiary.block || ''}<br />
                                    {selectedApiary.district}, {selectedApiary.state}
                                </div>
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ fontSize: '12px', color: '#b2bec3', textTransform: 'uppercase', fontWeight: 'bold' }}>Supervisor</label>
                                <div style={{ fontSize: '14px', color: '#636e72' }}>
                                    {selectedApiary.supervisor_name || 'N/A'}
                                </div>
                            </div>

                            <div style={{ marginBottom: '24px' }}>
                                <label style={{ fontSize: '12px', color: '#b2bec3', textTransform: 'uppercase', fontWeight: 'bold' }}>Stats</label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '4px' }}>
                                    <div style={{ background: '#f8f9fa', padding: '8px', borderRadius: '6px' }}>
                                        <div style={{ fontSize: '11px', color: '#636e72' }}>Hives</div>
                                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#2d3436' }}>
                                            {selectedApiary.original_units || selectedApiary.hive_count || 0}
                                        </div>
                                    </div>
                                    <div style={{ background: '#f8f9fa', padding: '8px', borderRadius: '6px' }}>
                                        <div style={{ fontSize: '11px', color: '#636e72' }}>Type</div>
                                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#2d3436' }}>
                                            {selectedApiary.apiary_type || 'Box'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <Link
                                to={`/nector-apiaries/${selectedApiary.apiary_id}`}
                                style={{
                                    display: 'block',
                                    background: '#ff8701',
                                    color: 'white',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    textDecoration: 'none',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    transition: 'background 0.2s'
                                }}
                            >
                                View Detailed Profile
                            </Link>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default React.memo(ApiaryMap);
